import { useEffect, useState, useCallback, useRef, useContext } from "react";
import { useParams, Prompt, useLocation, useHistory } from "react-router-dom";
import useAuthenticatedRequest from "../hooks/useAuthenticatedRestCall";
import AddEditCourseContent from "../components/AddEditCourseContent";
import { CONTENT_TYPES } from "../constants/contentTypes";
import QuizEditorContext from "../contexts/QuizEditorContext";
import {
  getContentUrl,
  getRichContentTranslationUrl,
  getRichContentUrl,
} from "../util/getUrls";
import { useTranslatedContentPayload } from "../hooks/useTranslatedContentPayload";
import { useInitializeTranslatedContent } from "../hooks/useInitializeTranslatedContent";
import { removeEmptyTranslations } from "../util/translationUtils";
import EditQuestionModal from "../components/courses/quizEditor/EditQuestionModal";
import { getDefaultQuestion } from "../components/courses/quizEditor/getDefaultQuestion";
import { DEFAULT_EDITOR_CONTENT } from "../constants/defaultEditorContent";
import { useDefaultLanguage } from "../hooks/useDefaultLanguage";
import LanguageContext from "../contexts/LanguageContext";
import { useKeycloak } from "@react-keycloak/web";
import { useSnackbar } from "notistack"; // Ensure this is imported

function EditCourseContent() {
  let { levelId, courseId, contentId } = useParams();
  const makeAuthenticatedRequest = useAuthenticatedRequest();
  const location = useLocation();
  const history = useHistory();
  const { keycloak } = useKeycloak();
  const { enqueueSnackbar } = useSnackbar(); // Access toast notification

  const tokenRef = useRef(keycloak?.token);
  useEffect(() => {
    tokenRef.current = keycloak?.token;
  }, [keycloak?.token]);

  const savedRef = useRef(false);
  const isNewContentRef = useRef(false);

  const { defaultLanguage, defaultLanguageLoaded } = useDefaultLanguage();
  const { languageInfo } = useContext(LanguageContext);
  const [languageBeingEditedId, setLanguageBeingEditedId] = useState("");

  useEffect(() => {
    if (defaultLanguageLoaded && !languageBeingEditedId) {
      setLanguageBeingEditedId(defaultLanguage.id);
    }
  }, [defaultLanguageLoaded, defaultLanguage, languageBeingEditedId]);

  const [title, setTitle] = useState("");
  const [published, setPublished] = useState(false);
  const [richContent, setRichContent] = useState(null);
  const [quillContents, setQuillContents] = useState(null);
  const [isQuiz, setIsQuiz] = useState(false);
  
  const [questions, setQuestions] = useState([]);
  const [originalQuestions, setOriginalQuestions] = useState([]); 

  const [version, setVersion] = useState(undefined);

  const [displayAllErrors, setDisplayAllErrors] = useState(false);
  const [emptyQuestions, setEmptyQuestions] = useState([]);
  const [emptyAnswers, setEmptyAnswers] = useState([]);
  const [translatedContent, setTranslatedContent] = useState({});
  const [questionTranslations, setQuestionTranslations] = useState([]);
  const [existingTranslatedTitles, setExistingTranslatedTitles] = useState(null);
  const [existingTranslations, setExistingTranslations] = useState([]);
  const [translatedTitles, setTranslatedTitles] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isNewQuestion, setIsNewQuestion] = useState(false);

  const hasAutoOpened = useRef(false);

  const { getTitleTranslations, getTranslatedRichContentPayload } =
    useTranslatedContentPayload(translatedTitles, translatedContent);

  useInitializeTranslatedContent(
    setTranslatedContent,
    setTranslatedTitles,
    existingTranslations,
    existingTranslatedTitles
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isNewQuiz = params.get("new") === "true";
    isNewContentRef.current = isNewQuiz;

    if (isNewQuiz && !hasAutoOpened.current && questions.length > 0) {
      const firstQuestion = questions[0];

      setSelectedQuestion(firstQuestion);
      setIsNewQuestion(true);
      setModalOpen(true);

      hasAutoOpened.current = true;
    }
  }, [questions, location.search]);

  useEffect(() => {
    return () => {
      if (isNewContentRef.current && !savedRef.current) {
        const deleteUrl = `${process.env.REACT_APP_COURSE_SERVICE_URL}/levels/${levelId}/courses/${courseId}/content/${contentId}`;
        
        if (tokenRef.current) {
            fetch(deleteUrl, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${tokenRef.current}`,
                    'Content-Type': 'application/json'
                },
                keepalive: true
            }).catch(err => console.error("Failed to clean up draft", err));
        }
      }
    };
  }, [levelId, courseId, contentId]); 

  const getRichContentTranslation = useCallback(
    async (languageId) => {
      const response = await makeAuthenticatedRequest(
        getRichContentTranslationUrl(levelId, courseId, contentId, languageId)
      );

      return { languageId: languageId, translatedValue: response.data };
    },
    [contentId, courseId, levelId, makeAuthenticatedRequest]
  );

  useEffect(() => {
    const fetchRichContent = async () =>
      makeAuthenticatedRequest(
        getRichContentUrl(levelId, courseId, contentId),
        true
      );

    const fetchContent = async () => {
      const response = await makeAuthenticatedRequest(
        getContentUrl(levelId, courseId, contentId),
        true
      );

      const thisContentIsAQuiz = response.data.type === CONTENT_TYPES.quiz;

      setTitle(response.data.title);
      setPublished(response.data.published);
      setIsQuiz(thisContentIsAQuiz);
      setExistingTranslatedTitles(response.data.titleTranslations);
      
      setVersion(response.data.__v);

      if (!thisContentIsAQuiz) {
        const existingTranslationsRequests =
          response.data.availableTranslations.map(getRichContentTranslation);

        const existingTranslationResponse = await Promise.all(
          existingTranslationsRequests
        );

        setExistingTranslations(existingTranslationResponse);

        const richContentToDisplay = await fetchRichContent();
        if (richContentToDisplay && richContentToDisplay.data) {
          setRichContent(richContentToDisplay.data);
        } else {
          setRichContent({ content: DEFAULT_EDITOR_CONTENT });
        }
      } else {
        setQuestions(response.data.questions);
        setOriginalQuestions(JSON.parse(JSON.stringify(response.data.questions)));
      }
    };

    fetchContent();
  }, [
    makeAuthenticatedRequest,
    levelId,
    courseId,
    contentId,
    getRichContentTranslation,
  ]);

  const handleEditClick = (question) => {
    setSelectedQuestion(question);
    setIsNewQuestion(false);
    setModalOpen(true);
  };

  const handleTitleChange = (value) => {
    setTitle(value);
    setIsDirty(true);
  };

  const getAnswersWithoutEmptyTranslations = (answers) =>
    answers.map((answer) => ({
      ...answer,
      answerTextTranslations: removeEmptyTranslations(
        answer.answerTextTranslations
      ),
      feedbackTextTranslations: removeEmptyTranslations(
        answer.feedbackTextTranslations
      ),
    }));

  const getQuestionsWithoutEmptyTranslations = (currentQuestions) =>
    currentQuestions.map((question) => ({
      ...question,
      id: question.id, 
      questionTextTranslations: removeEmptyTranslations(
        question.questionTextTranslations
      ),
      answers: getAnswersWithoutEmptyTranslations(question.answers),
    }));

  const getPayload = (overrides = {}) => {
    const translationInfo = {
      titleTranslations: getTitleTranslations(),
      richContentTranslations: getTranslatedRichContentPayload(),
    };

    const titleToSave = overrides.title !== undefined ? overrides.title : title;
    const publishedToSave =
      overrides.published !== undefined ? overrides.published : published;

    const questionsToSave = overrides.questions || questions;

    const basePayload = {
      __v: version
    };

    if (!isQuiz) {
      return {
        ...basePayload,
        title: titleToSave,
        published: publishedToSave,
        ...translationInfo,
      };
    }

    return {
      ...basePayload,
      title: titleToSave,
      published: publishedToSave,
      questions: getQuestionsWithoutEmptyTranslations(questionsToSave),
      ...translationInfo,
    };
  };

  const saveContent = (overrides = {}) => {
    return makeAuthenticatedRequest(
      `${process.env.REACT_APP_COURSE_SERVICE_URL}/levels/${levelId}/courses/${courseId}/content/${contentId}`,
      true,
      {
        data: getPayload(overrides),
        method: "PUT",
      }
    ).then((response) => {
      if (response && response.status === 200) {
        setIsDirty(false);
        savedRef.current = true;

        if (isQuiz && response.data.questions) {
            setQuestions(response.data.questions);
            setOriginalQuestions(JSON.parse(JSON.stringify(response.data.questions)));
        }
        
        if (response.data.__v !== undefined) {
            setVersion(response.data.__v);
        }

        if (overrides.title !== undefined) setTitle(overrides.title);
        if (overrides.published !== undefined) setPublished(overrides.published);
      }
      return response;
    });
  };

  const handleRichContentEdited = useCallback((updatedContents) => {
    setQuillContents(updatedContents);
    setIsDirty(true);
  }, []);

  const handlePublishedChanged = (event) => {
    const newPublishedState = event.target.checked;
    setPublished(newPublishedState);
    saveContent({ published: newPublishedState });
  };

  const getLoading = () =>
    isQuiz ? questions.length === 0 : richContent === null;

  const handleAddNewQuestionClick = () => {
    const newQuestion = getDefaultQuestion();
    setSelectedQuestion(newQuestion);
    setIsNewQuestion(true);
    setModalOpen(true);
  };

  const handleQuestionUpdate = (updatedQuestion) => {
    const questionIndex = questions.findIndex(
      (q) => q.id === selectedQuestion.id
    );
    const updatedQuestions = [...questions];

    if (questionIndex !== -1) {
      updatedQuestions[questionIndex] = {
        ...updatedQuestion,
        id: selectedQuestion.id,
      };
    } else {
      updatedQuestions.push(updatedQuestion);
    }

    setQuestions(updatedQuestions);
    saveContent({ questions: updatedQuestions });
  };

  // FIX: Prevent deleting the last question
  const handleDeleteQuestion = (questionToDelete) => {
    if (questions.length <= 1) {
        enqueueSnackbar("Quizzes must have at least one question.", { variant: "warning" });
        return;
    }

    if (window.confirm("Are you sure you want to delete this question?")) {
        const updatedQuestions = questions.filter(q => q.id !== questionToDelete.id);
        setQuestions(updatedQuestions);

        setEmptyQuestions(emptyQuestions.filter((qid) => qid !== questionToDelete.id));
        const answerIds = questionToDelete.answers.map((a) => a.id);
        setEmptyAnswers(emptyAnswers.filter((aid) => !answerIds.includes(aid)));

        saveContent({ questions: updatedQuestions });
    }
  };

  const handleSetQuestions = (newQuestions) => {
    setQuestions(newQuestions);
    setIsDirty(true);
  };

  const handleDiscardChanges = () => {
    setQuestions(JSON.parse(JSON.stringify(originalQuestions)));
    setIsDirty(false);
  };

  const initialQuizContextValue = {
    questions,
    setQuestions: handleSetQuestions,
    emptyQuestions,
    setEmptyQuestions,
    emptyAnswers,
    setEmptyAnswers,
    displayAllErrors,
    setDisplayAllErrors,
    questionTranslations,
    setQuestionTranslations,
  };

  const handleTitleSave = (newTitle) => {
    saveContent({ title: newTitle });
  };

  return (
    <QuizEditorContext.Provider value={initialQuizContextValue}>
      <>
        <Prompt
          when={isDirty}
          message="Unsaved changes will be lost. Leave anyway?"
        />
        <AddEditCourseContent
          title={title}
          onTitleChange={handleTitleChange}
          isLoading={getLoading()}
          onRichContentEdited={handleRichContentEdited}
          initialRichContent={richContent?.content}
          published={published}
          onPublishedChanged={handlePublishedChanged}
          isQuiz={isQuiz}
          showQuizModeToggle={false}
          saveRequest={saveContent}
          quillContents={quillContents}
          translatedContent={translatedContent}
          setTranslatedContent={setTranslatedContent}
          saveSuccessMessage={`${title} updated successfully!`}
          translatedTitles={translatedTitles}
          setTranslatedTitles={setTranslatedTitles}
          onTitleSave={handleTitleSave}
          hideSaveButton={isQuiz}
          languageBeingEditedId={languageBeingEditedId}
          setLanguageBeingEditedId={setLanguageBeingEditedId}
          onEditQuestion={handleEditClick}
          onAddQuestion={handleAddNewQuestionClick}
          onDeleteQuestion={handleDeleteQuestion}
          isDirty={isDirty}
          onDiscardChanges={handleDiscardChanges}
        />

        <EditQuestionModal
          open={modalOpen}
          question={selectedQuestion}
          onClose={() => setModalOpen(false)}
          onSave={handleQuestionUpdate}
          isNewQuestion={isNewQuestion}
          languageBeingEditedId={languageBeingEditedId}
          defaultLanguageId={defaultLanguage?.id}
        />
      </>
    </QuizEditorContext.Provider>
  );
}

export default EditCourseContent;